<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { writable, get } from 'svelte/store';
  import { theme } from '$stores/themeStore';
  import { userRole, isReadOnly } from '$stores/sessionStore';
  
  // Using proxy endpoints instead of direct API access 
  const API_BASE_URL = '/api/proxy';

  const dispatch = createEventDispatcher();
  export let showForm = false;
  export let editStudy = null;
  /**
   * Error returned by the save itself, set by the parent. The uniqueness
   * pre-check now fails open, so the server is the authority on duplicates —
   * its rejection has to be visible or a real duplicate would fail silently.
   */
  export let submitError = '';

  let studyData = writable({
    _id: null,
    studyId: null,
    studyName: '',
    protocolNumber: '',
    externalId: '',
    statusGUID: '075e70d8c67b11ecbac01636',
    therapeuticAreaGUID: '568db412383aa535c61ade4c',
    projectNumber: '',
    sponsor: '',
    studyPhase: '',
    startDate: '',
    stopDate: '',
    active: true,
    createUserGUID: null,
    createDate: null,
    lastUpdateUserGUID: null,
    lastUpdateDate: null,
    description: '',
  });

  // Store original data for change detection
  let originalStudyData = null;
  let hasChanges = false;

  let statusOptions = [];

  // Error states
  let protocolNumberError = '';
  let studyNameError = '';

  onMount(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/lookups/STAT`);
      const result = await response.json();
      statusOptions = result.data;
    } catch (error) {
      console.error('Error fetching status options:', error);
    }
  });

  $: if (editStudy && showForm) {
    const formattedStudy = { ...editStudy };
    if (editStudy.startDate) {
      const date = new Date(editStudy.startDate);
      formattedStudy.startDate = date.toLocaleDateString('en-CA');
    }
    if (editStudy.stopDate) {
      const date = new Date(editStudy.stopDate);
      formattedStudy.stopDate = date.toLocaleDateString('en-CA');
    }
    studyData.set(formattedStudy);
    // Store original data for change detection
    originalStudyData = JSON.parse(JSON.stringify(formattedStudy));
    hasChanges = false;
  }

  $: if (showForm && !editStudy) {
    resetStudyData();
    originalStudyData = null;
    hasChanges = false;
  }

  // Detect changes by comparing current data with original data
  $: if ($studyData) {
    if (originalStudyData) {
      // Editing existing study - compare with original data
      const currentData = JSON.parse(JSON.stringify($studyData));
      // Remove fields that shouldn't be compared for changes
      delete currentData.createUserGUID;
      delete currentData.createDate;
      delete currentData.lastUpdateUserGUID;
      delete currentData.lastUpdateDate;
      
      const originalData = JSON.parse(JSON.stringify(originalStudyData));
      delete originalData.createUserGUID;
      delete originalData.createDate;
      delete originalData.lastUpdateUserGUID;
      delete originalData.lastUpdateDate;
      
      hasChanges = JSON.stringify(currentData) !== JSON.stringify(originalData);
    } else {
      // Creating new study - check if required fields have values
      const hasRequiredFields = $studyData.protocolNumber?.trim() || 
                               $studyData.studyName?.trim() || 
                               $studyData.sponsor?.trim() || 
                               $studyData.projectNumber?.trim() || 
                               $studyData.studyPhase?.trim() || 
                               $studyData.startDate || 
                               $studyData.stopDate || 
                               $studyData.description?.trim();
      hasChanges = !!hasRequiredFields;
    }
  }

  function resetStudyData() {
    studyData.set({
      _id: null,
      studyId: null,
      studyName: '',
      protocolNumber: '',
      externalId: '',
      statusGUID: '075e70d8c67b11ecbac01636',
      therapeuticAreaGUID: '568db412383aa535c61ade4c',
      projectNumber: '',
      sponsor: '',
      studyPhase: '',
      startDate: '',
      stopDate: '',
      active: true,
      createUserGUID: null,
      createDate: null,
      lastUpdateUserGUID: null,
      lastUpdateDate: null,
      description: '',
    });
  }

  function close() {
    // Reset change detection when closing
    originalStudyData = null;
    hasChanges = false;
    dispatch('close');
  }

  async function isProtocolNumberUnique(protocolNumber) {
    try {
      const excludeId = editStudy?._id || '';
      const url = excludeId 
        ? `${API_BASE_URL}/studies/validate/protocolNumber/${encodeURIComponent(protocolNumber)}?excludeId=${excludeId}`
        : `${API_BASE_URL}/studies/validate/protocolNumber/${encodeURIComponent(protocolNumber)}`;
      
      const response = await fetch(url);

      // Check the status BEFORE parsing: an error response may have no body at
      // all, and .json() would throw before we ever look at response.ok.
      if (!response.ok) {
        console.error('Protocol number uniqueness check failed:', response.status);
        return true;
      }

      const result = await response.json();
      return result.isUnique !== false;
    } catch (error) {
      // Fail OPEN. Returning false here means "not unique", which surfaced as
      // "This Protocol Number is already in use" on a study whose number was
      // perfectly fine — an error the user cannot clear. createStudy and
      // updateStudy both reject a real duplicate (excluding the study being
      // edited), so the server stays the authority on uniqueness.
      console.error('Error checking protocol number uniqueness:', error);
      return true;
    }
  }

  async function isStudyNameUnique(studyName) {
    try {
      const excludeId = editStudy?._id || '';
      const url = excludeId 
        ? `${API_BASE_URL}/studies/validate/studyName/${encodeURIComponent(studyName)}?excludeId=${excludeId}`
        : `${API_BASE_URL}/studies/validate/studyName/${encodeURIComponent(studyName)}`;
      
      const response = await fetch(url);

      if (!response.ok) {
        console.error('Study name uniqueness check failed:', response.status);
        return true;
      }

      const result = await response.json();
      return result.isUnique !== false;
    } catch (error) {
      // Fail OPEN — see isProtocolNumberUnique. updateStudy rejects a genuine
      // duplicate name, so a failed pre-check must not block a valid save.
      console.error('Error checking study name uniqueness:', error);
      return true;
    }
  }

  async function validateForm() {
    let isValid = true;

    // Reset errors at start of validation
    protocolNumberError = '';
    studyNameError = '';

    // Validate Protocol Number
    if (!$studyData.protocolNumber || !$studyData.protocolNumber.trim()) {
      protocolNumberError = 'Protocol Number is required';
      isValid = false;
    } else {
      try {
        const isUnique = await isProtocolNumberUnique($studyData.protocolNumber.trim());
        if (!isUnique) {
          protocolNumberError = 'This Protocol Number is already in use';
          isValid = false;
        }
      } catch (error) {
        console.error('Error in protocol number validation:', error);
        protocolNumberError = 'Error validating Protocol Number';
        isValid = false;
      }
    }

    // Validate Study Name
    if (!$studyData.studyName || !$studyData.studyName.trim()) {
      studyNameError = 'Study Name is required';
      isValid = false;
    } else {
      try {
        const isUnique = await isStudyNameUnique($studyData.studyName.trim());
        if (!isUnique) {
          studyNameError = 'This Study Name is already in use';
          isValid = false;
        }
      } catch (error) {
        console.error('Error in study name validation:', error);
        studyNameError = 'Error validating Study Name';
        isValid = false;
      }
    }

    return isValid;
  }

  async function handleSubmit() {
    submitError = '';
    if (!await validateForm()) {
      return;
    }

    const study = get(studyData);

    // Trim the protocol number and study name
    study.protocolNumber = study.protocolNumber?.trim() || '';
    study.studyName = study.studyName?.trim() || '';

    if (study.startDate) {
      const [year, month, day] = study.startDate.split('-');
      study.startDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
    }
    if (study.stopDate) {
      const [year, month, day] = study.stopDate.split('-');
      study.stopDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();
    }

    // Ensure required fields are present
    if (!study.statusGUID) {
      study.statusGUID = '075e70d8c67b11ecbac01636';
    }
    if (!study.therapeuticAreaGUID) {
      study.therapeuticAreaGUID = '568db412383aa535c61ade4c';
    }

    dispatch('submit', study);
    close();
  }

  // Clear errors when fields change
  $: if ($studyData.protocolNumber) {
    protocolNumberError = '';
  }

  $: if ($studyData.studyName) {
    studyNameError = '';
  }
</script>

{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={close}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div 
      class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-3xl relative z-60"
      on:click|stopPropagation
    >
      <header class="flex justify-between items-start mb-4">
        <div class="flex items-center gap-2">
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {editStudy ? 'Edit Study' : 'New Study'}
          </h2>
          {#if editStudy && hasChanges}
            <span class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full dark:bg-yellow-900 dark:text-yellow-200">
              Modified
            </span>
          {/if}
        </div>
        <button
          on:click={close}
          class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          &times;
        </button>
      </header>
      <form on:submit|preventDefault={handleSubmit} class="space-y-4">
        <div class="flex gap-3 pt-4">
          <div class="w-2/4">
            <label
              for="protocolName"
              class="block text-xs pb-1 text-gray-700 dark:text-gray-200">Protocol *</label
            >
            <input
              type="text"
              id="protocolName"
              bind:value={$studyData.protocolNumber}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {protocolNumberError ? 'border-red-500' : ''}"
              required
            />
            {#if protocolNumberError}
              <p class="text-red-500 text-xs mt-1">{protocolNumberError}</p>
            {/if}
          </div>
          <div class="w-1/4">
            <label for="externalId" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >External Id</label
            >
            <input
              type="text"
              id="externalId"
              bind:value={$studyData.externalId}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          <div>
            <label for="status" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Status *</label
            >
            <select
              id="status"
              bind:value={$studyData.statusGUID}
              class="form-select h-8 w-24 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              {#each statusOptions as option}
                <option value={option.id}>{option.label}</option>
              {/each}
            </select>
          </div>
          <div>
            <label for="active" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Active *</label
            >
            <select
              id="active"
              bind:value={$studyData.active}
              class="form-select h-8 w-16 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
            >
              <option value={true}>Yes</option>
              <option value={false}>No</option>
            </select>
          </div>
        </div>
        <div class="flex gap-3">
          <div class="w-full">
            <label for="name" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Name *</label
            >
            <input
              type="text"
              id="name"
              bind:value={$studyData.studyName}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 {studyNameError ? 'border-red-500' : ''}"
              required
            />
            {#if studyNameError}
              <p class="text-red-500 text-xs mt-1">{studyNameError}</p>
            {/if}
          </div>
        </div>
        <div class="flex gap-3 pt-6">
          <div class="w-full">
            <label for="sponsor" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Sponsor</label
            >
            <input
              type="text"
              id="sponsor"
              bind:value={$studyData.sponsor}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          <div class="w-1/2" hidden>
            <label for="therapyArea" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Therapy Area</label
            >
            <input
              type="text"
              id="therapyArea"
              bind:value={$studyData.therapeuticAreaGUID}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
        </div>
        <div class="flex gap-3">
          <div class="w-1/2">
            <label for="projectNumber" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Project Number</label
            >
            <input
              type="text"
              id="projectNumber"
              bind:value={$studyData.projectNumber}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          <div class="w-1/2">
            <label for="studyPhase" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Study Phase</label
            >
            <input
              type="text"
              id="studyPhase"
              bind:value={$studyData.studyPhase}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
        </div>
        <div class="flex gap-3">
          <div class="w-1/2">
            <label for="startDate" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Start Date</label
            >
            <input
              type="date"
              id="startDate"
              bind:value={$studyData.startDate}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
          <div class="w-1/2">
            <label for="endDate" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >End Date</label
            >
            <input
              type="date"
              id="endDate"
              bind:value={$studyData.stopDate}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            />
          </div>
        </div>
        <div class="flex gap-3">
          <div class="w-full">
            <label for="description" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Description</label
            >
            <textarea
              id="description"
              bind:value={$studyData.description}
              class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              rows="3"
            ></textarea>
          </div>
        </div>
        {#if submitError}
          <p class="text-red-600 dark:text-red-400 text-sm pt-4" role="alert">{submitError}</p>
        {/if}
        <div class="flex justify-end space-x-2 pt-10">
          <button
            type="button"
            on:click={close}
            class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700">Cancel</button
          >
          <button
            type="submit"
            class="py-1 px-2 rounded {isReadOnly() || !hasChanges ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-700'}"
            disabled={isReadOnly() || !hasChanges}
          >
            {hasChanges ? 'Save' : 'Save'}
          </button
          >
        </div>
      </form>
    </div>
  </div>
{/if}
